import React, {useCallback, useEffect, useRef} from 'react';
import ReactDOM from 'react-dom';
import mapboxgl from 'mapbox-gl';
import { Provider, useDispatch, useSelector } from 'react-redux';

import { map } from './Map';
import { deviceCategories } from '../common/deviceCategories';
import store, { devicesActions } from '../store';
import { useHistory } from 'react-router-dom';
import StatusView from './StatusView';
import Snackbar from "@material-ui/core/Snackbar";
import Alert from "@material-ui/lab/Alert";
import StartsStopsView from "./StartsStopsView";
import {ThemeProvider} from "@material-ui/core/styles";
import {useTheme} from "@material-ui/core";

const PositionsMap = ({ positions, replayBack }) => {
  const id = 'positions';
  const clusters = `${id}-clusters`;

  const theme = useTheme();
  const history = useHistory();
    const dispatch = useDispatch();

  const devices = useSelector(state => state.devices.items);
  const mapType = useSelector(state => state.session.user && state.session.user.map);

  const currentPos  = useRef(null);
  const activePopupRef = useRef(null);
  const activeDeviceIdRef = useRef(null);
  const checkOneTime = useRef(false);

  const lastCourseRef = useRef({});
  const prevCoordsRef = useRef({});

  const bearing = (lat1, lon1, lat2, lon2) => {
    const toRad = deg => deg * Math.PI / 180;
    const toDeg = rad => rad * 180 / Math.PI;
    const dLon = toRad(lon2 - lon1);
    const y = Math.sin(dLon) * Math.cos(toRad(lat2));
    const x = Math.cos(toRad(lat1)) * Math.sin(toRad(lat2))
        - Math.sin(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.cos(dLon);
    return (toDeg(Math.atan2(y, x)) + 360) % 360;
  };

  const [openSnackbar, setOpenSnackbar] = React.useState(true);

  currentPos.current = positions[0];

  const deviceColor = device => {
    switch (device.status) {
      case 'online':
        return 'green';
      case 'offline':
        return 'gray';
      case 'slowing':
        return 'yellow';
      default:
        return 'red';
    }
  };

  const createFeature = (devices, position) => {
    const device = devices[position.deviceId];
    const did = position.deviceId;
    const prev = prevCoordsRef.current[did];
    let course;
    if (device.category === 'default' || device.category === null) {
        course = 0;
    } else if (prev && (prev.lat !== position.latitude || prev.lon !== position.longitude)) {
      course = bearing(prev.lat, prev.lon, position.latitude, position.longitude);
    } else if (position.attributes && typeof position.attributes.course === 'number') {
      course = position.attributes.course;
    } else {
      course = lastCourseRef.current[did] || 0;
    }
    lastCourseRef.current[did] = course;
    prevCoordsRef.current[did] = { lat: position.latitude, lon: position.longitude };
    const feature = {
      deviceId: did,
      name: device.name,
      category: deviceCategories.includes(device.category) ? `${device.category}Position` : 'defaultPosition',
      color: deviceColor(device),
      course: course,
    };
    return feature;
  };

  const onMouseEnter = () => map.getCanvas().style.cursor = 'pointer';
  const onMouseLeave = () => map.getCanvas().style.cursor = '';

  const onMarkerClick = useCallback(event => {
    window.dispatchEvent(new CustomEvent('close-devices-list-info-window'));
    const feature = event.features[0];
    const deviceId = feature.properties.deviceId; // Get the device ID
    let coordinates = feature.geometry.coordinates.slice();

    while (Math.abs(event.lngLat.lng - coordinates[0]) > 180)
      coordinates[0] += event.lngLat.lng > coordinates[0] ? 360 : -360;

    if (activePopupRef.current)
      activePopupRef.current.remove();

    const placeholder = document.createElement('div');
    ReactDOM.render(
        <Provider store={store}>
          {!replayBack ?
              <ThemeProvider theme={theme}>
                <StatusView deviceId={feature.properties.deviceId} onShowDetails={positionId => history.push(`/position/${positionId}`)} history={history}/>
              </ThemeProvider>
              :
              <ThemeProvider theme={theme}>
                <StartsStopsView position={currentPos.current} status={"positionFixTime"} deviceName={devices[currentPos.current.deviceId].name} mapType={mapType}/>
              </ThemeProvider>
          }
        </Provider>,
        placeholder
    );

    const popup = new mapboxgl.Popup({
      offset: 25,
      anchor: 'top'
    })
        .setDOMContent(placeholder)
        .setLngLat(coordinates)
        .addTo(map);
    activePopupRef.current = popup;
    activeDeviceIdRef.current = deviceId;
    dispatch(devicesActions.select({ id: deviceId }));

    popup.on('close', () => {
      ReactDOM.unmountComponentAtNode(placeholder);
      activePopupRef.current = null;
      activeDeviceIdRef.current = null;
      dispatch(devicesActions.select({ id: null }));

    });
  }, [history, theme]);

  const onClusterClick = event => {
    const features = map.queryRenderedFeatures(event.point, {
      layers: [clusters],
    });
    const clusterId = features[0].properties.cluster_id;
    map.getSource(id).getClusterExpansionZoom(clusterId, (error, zoom) => {
      if (!error) map.easeTo({
        center: features[0].geometry.coordinates,
        zoom: zoom,
      });
    });
  };

    useEffect(() => {
        const onCloseMapInfoWindow = () => {
            if (activePopupRef.current) {
                activePopupRef.current.remove();
                activePopupRef.current = null;
                activeDeviceIdRef.current = null;
                dispatch(devicesActions.select({ id: null }));
            }
        };
        window.addEventListener('close-map-info-window', onCloseMapInfoWindow);
        return () => {
            window.removeEventListener('close-map-info-window', onCloseMapInfoWindow);
        };
    }, []);

  useEffect(() => {
        map.addSource(id, {
          'type': 'geojson',
          'data': {
            type: 'FeatureCollection',
            features: [],
          },
          'cluster': true,
          'clusterMaxZoom': 14,
          'clusterRadius': 50,
        });
        map.addLayer({
          'id': id,
          'type': 'symbol',
          'source': id,
          'filter': ['!', ['has', 'point_count']],
          'layout': {
            'icon-image': '{category}-{color}',
            'icon-allow-overlap': true,
            'icon-rotate': ['get', 'course'],
            'icon-rotation-alignment': 'map',
            'text-field': '{name}',
            'text-allow-overlap': true,
            'text-anchor': 'bottom',
            'text-offset': [0, -2],
            'text-font': ['Roboto Regular'],
            'text-size': 12,
          },
          'paint': {
            'text-halo-color': 'white',
            'text-halo-width': 1,
          },
        });
        map.addLayer({
          'id': clusters,
          'type': 'symbol',
          'source': id,
          'filter': ['has', 'point_count'],
          'layout': {
            'icon-image': 'background',
            'text-field': '{point_count_abbreviated}',
            'text-font': ['Roboto Regular'],
            'text-size': 14,
          },
        });


        map.on('mouseenter', id, onMouseEnter);
        map.on('mouseleave', id, onMouseLeave);
        map.on('mouseenter', clusters, onMouseEnter);
        map.on('mouseleave', clusters, onMouseLeave);
        map.on('click', id, onMarkerClick);
        map.on('click', clusters, onClusterClick);

        return () => {
          Array.from(map.getContainer().getElementsByClassName('mapboxgl-popup')).forEach(el => el.remove());

          activePopupRef.current = null;
          activeDeviceIdRef.current = null;

          map.off('mouseenter', id, onMouseEnter);
          map.off('mouseleave', id, onMouseLeave);
          map.off('mouseenter', clusters, onMouseEnter);
          map.off('mouseleave', clusters, onMouseLeave);
          map.off('click', id, onMarkerClick);
          map.off('click', clusters, onClusterClick);

          map.removeLayer(id);
          map.removeLayer(clusters);
          map.removeSource(id);
        };
      },
      [onMarkerClick, theme]);


  useEffect(() => {
    const updateMap = async () => {
      setOpenSnackbar(true);
      const source = map.getSource(id);
      if (source) {
        await new Promise(resolve => {
          source.setData({
            type: 'FeatureCollection',
            features: positions
                .filter(it => devices.hasOwnProperty(it.deviceId))
                .map(position => ({
                  type: 'Feature',
                  geometry: {
                    type: 'Point',
                    coordinates: [position.longitude, position.latitude],
                  },
                  properties: createFeature(devices, position),
                })),
          });
          requestAnimationFrame(() => resolve());
        });
        if (activePopupRef.current && activeDeviceIdRef.current) {
          const currentOpenPosition = positions.find(p => p.deviceId === activeDeviceIdRef.current);
          if (currentOpenPosition) {
            activePopupRef.current.setLngLat([currentOpenPosition.longitude, currentOpenPosition.latitude]);
          }
        }
      }
      const coordinates = positions.map(item => [item.longitude, item.latitude]);
      if (coordinates.length && !checkOneTime.current) {
        const bounds = coordinates.reduce((bounds, item) => bounds.extend(item), new mapboxgl.LngLatBounds(coordinates[0], coordinates[0]));
        map.fitBounds(bounds, {padding: { top: 50, bottom: 250, left: 25, right: 25 },});
        checkOneTime.current = true;
      }
    };
    updateMap();
  }, [devices, positions, theme]);

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };
  function snackbarColor(){
    return "error";
  }
  function message(id){
    return 'The devices "'+ id +'" have a poor signals '
  }
  function gpsSgnTest() {
    let device = ""
    let  i = 0
    if (positions.length > 0 && devices.length > 0){
      positions.forEach(position => {
        if (position.attributes.gpsSgn < 3) {
          i++;
          if (i > 1)
            device = device + " and " + devices[position.deviceId].name
          else
            device = devices[position.deviceId].name
        }
      })
      return  device;
    }else
      return "";
  }

  useEffect(() => {
    const styleId = 'mapbox-cyber-popup';
    let styleElement = document.getElementById(styleId);

    if (!styleElement) {
      styleElement = document.createElement('style');
      styleElement.id = styleId;
      document.head.appendChild(styleElement);
    }

    const isDarkMode = theme.palette.typeBackground === 'dark';

    const popupBg = isDarkMode ? 'rgba(15, 25, 35, 0.7)' : 'rgba(255, 255, 255, 0.5)';

    const glowOpacity = isDarkMode ? '0.4' : '0.2';

    styleElement.innerHTML = `
      .mapboxgl-popup-content {
        background: ${popupBg} !important;
        backdrop-filter: blur(10px) !important;
        -webkit-backdrop-filter: blur(10px) !important;
        border: 1px solid #00f2ff !important;
        box-shadow: 0 0 20px rgba(0, 242, 255, ${glowOpacity}) !important;
        border-radius: 20px !important;
        padding: 0 !important;
      }
      .mapboxgl-popup-tip {
        border-top-color: #00f2ff !important;
        border-bottom-color: #00f2ff !important;
        opacity: 0.6;
      }
      .mapboxgl-popup-close-button {
        color: #00f2ff !important;
        font-size: 16px !important;
        padding: 5px 8px !important;
        outline: none !important;
      }
      .mapboxgl-popup-close-button:hover {
        background-color: rgba(0, 242, 255, 0.1) !important;
        border-radius: 0 12px 0 0;
      }
    `;

    return () => {
      // If you want the style to disappear when the component unmounts:
      // if (styleElement) styleElement.remove();
    };
  }, [theme]);

  return (
      <>
        {openSnackbar && gpsSgnTest() !== "" ?
            <Snackbar open={openSnackbar} autoHideDuration={60000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'center' }} >
              <Alert onClose={handleCloseSnackbar } variant="filled" severity={snackbarColor()}>
                {message(gpsSgnTest())}
              </Alert>
            </Snackbar>
            :null}
      </>
  );
}

export default PositionsMap;
